import { DecimalPipe, DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { ChartData, ChartOptions } from 'chart.js';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { UIChart } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { AuthService } from '../../../core/auth/auth.service';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import {
  AccountingDashboardData,
  AccountingDateRange,
  currentAccountingMonth,
  validDateRange,
} from '../models/accounting-dashboard.model';
import { AccountingDashboardService } from '../services/accounting-dashboard.service';

const toPickerDate = (key: string): Date => new Date(`${key}T00:00:00`);
const toDateKey = (date: Date | null): string =>
  date && Number.isFinite(date.getTime())
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : '';

interface AccountingChartTheme {
  income: string;
  expense: string;
  commission: string;
  refund: string;
  surface: string;
  text: string;
  mutedText: string;
  border: string;
  fontFamily: string;
  bodyFontSize: number;
  supportingFontSize: number;
  reducedMotion: boolean;
}

@Component({
  selector: 'app-accounting-dashboard',
  imports: [
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    ButtonModule,
    DatePickerModule,
    UIChart,
    RefreshButtonComponent,
  ],
  templateUrl: './accounting-dashboard.component.html',
})
export class AccountingDashboardComponent implements OnInit {
  private readonly dashboardService = inject(AccountingDashboardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly overlay = inject(GlobalOverlayService);
  readonly chartTheme = signal<AccountingChartTheme | null>(null);
  readonly auth = inject(AuthService);
  readonly isLoading = signal(false);
  readonly error = signal('');
  readonly dashboard = signal<AccountingDashboardData | null>(null);
  readonly range = signal<AccountingDateRange>(currentAccountingMonth());
  readonly pendingDateFrom = signal<Date | null>(toPickerDate(this.range().dateFrom));
  readonly pendingDateTo = signal<Date | null>(toPickerDate(this.range().dateTo));
  readonly summary = computed(() => this.dashboard()?.summary);
  readonly incomeTotal = computed(() => this.summary()?.incomeAmount ?? 0);
  readonly expenseTotal = computed(() => this.summary()?.expenseAmount ?? 0);
  readonly incomeCount = computed(() => this.summary()?.incomeCount ?? 0);
  readonly expenseCount = computed(() => this.summary()?.expenseCount ?? 0);
  readonly transactionCount = computed(() => this.incomeCount() + this.expenseCount());
  readonly netTotal = computed(() => this.incomeTotal() - this.expenseTotal());
  readonly transferCount = computed(() => this.summary()?.pendingTransferCount ?? 0);
  readonly transferTotal = computed(() => this.summary()?.pendingTransferAmount ?? 0);
  readonly buckets = computed(() => this.dashboard()?.cashFlow ?? []);
  readonly monthlyChart = computed(
    () =>
      (Date.parse(this.range().dateTo) - Date.parse(this.range().dateFrom)) / 86_400_000 + 1 > 62,
  );
  readonly barData = computed<ChartData<'bar', number[], string>>(() => ({
    labels: this.buckets().map((item) => item.label),
    datasets: [
      {
        label: ' เงินรับ',
        data: this.buckets().map((item) => item.income),
        backgroundColor: this.chartTheme()?.income,
        hoverBackgroundColor: this.chartTheme()?.income,
        borderRadius: 4,
        maxBarThickness: 18,
      },
      {
        label: ' เงินจ่าย',
        data: this.buckets().map((item) => item.expense),
        backgroundColor: this.chartTheme()?.expense,
        hoverBackgroundColor: this.chartTheme()?.expense,
        borderRadius: 4,
        maxBarThickness: 18,
      },
    ],
  }));
  readonly expenseBreakdown = computed(() =>
    [
      {
        category: 'EXPENSE', label: ' ค่าใช้จ่าย', color: this.chartTheme()?.expense,
        amount: this.dashboard()?.expenseBreakdown.expenseAmount ?? 0,
      },
      {
        category: 'COMMISSION', label: ' คอมมิชชั่น', color: this.chartTheme()?.commission,
        amount: this.dashboard()?.expenseBreakdown.commissionAmount ?? 0,
      },
      {
        category: 'REFUND', label: ' เงินคืนลูกค้า', color: this.chartTheme()?.refund,
        amount: this.dashboard()?.expenseBreakdown.refundAmount ?? 0,
      },
    ].map((group) => {
      return {
        ...group,
        percentage: this.expenseTotal() ? (group.amount / this.expenseTotal()) * 100 : 0,
      };
    }),
  );
  readonly doughnutData = computed<ChartData<'doughnut', number[], string>>(() => ({
    labels: this.expenseBreakdown().map((item) => item.label),
    datasets: [
      {
        data: this.expenseBreakdown().map((item) => item.amount),
        backgroundColor: this.expenseBreakdown().map((item) => item.color ?? ''),
        hoverBackgroundColor: this.expenseBreakdown().map((item) => item.color ?? ''),
        borderWidth: 4,
        borderColor: this.chartTheme()?.surface,
      },
    ],
  }));
  readonly barOptions = computed<ChartOptions<'bar'>>(() => ({
    ...this.chartPresentation(),
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: this.chartTheme()?.mutedText,
          maxTicksLimit: 8,
          maxRotation: 0,
          font: {
            family: this.chartTheme()?.fontFamily,
            size: this.chartTheme()?.supportingFontSize,
            lineHeight: 1.5,
          },
        },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: this.chartTheme()?.border },
        ticks: {
          color: this.chartTheme()?.mutedText,
          maxTicksLimit: 5,
          font: {
            family: this.chartTheme()?.fontFamily,
            size: this.chartTheme()?.supportingFontSize,
            lineHeight: 1.5,
          },
          callback: (value) =>
            new Intl.NumberFormat('th-TH', { notation: 'compact' }).format(Number(value)),
        },
      },
    },
  }));
  readonly doughnutOptions = computed<ChartOptions<'doughnut'>>(() => ({
    ...this.chartPresentation(),
    cutout: '74%',
  }));
  readonly rangeLabel = computed(() => {
    const format = new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return `${format.format(new Date(this.range().dateFrom))} – ${format.format(new Date(this.range().dateTo))}`;
  });

  constructor() {
    afterNextRender(() => {
      const view = this.document.defaultView;
      if (!view) return;
      const styles = view.getComputedStyle(this.document.documentElement);
      const pageText = this.document.querySelector<HTMLElement>('.page-head p');
      const typography = view.getComputedStyle(pageText ?? this.document.documentElement);
      const bodyFontSize = Number.parseFloat(typography.fontSize) || 14;
      const color = (token: string, fallback: string) =>
        styles.getPropertyValue(token).trim() || styles.getPropertyValue(fallback).trim();
      this.chartTheme.set({
        income: color('--p-primary-500', '--theme-color'),
        expense: color('--p-primary-800', '--theme-ink'),
        commission: color('--p-primary-600', '--theme-color'),
        refund: color('--p-primary-300', '--theme-border'),
        surface: color('--p-content-background', '--color-white'),
        text: color('--p-text-color', '--theme-ink'),
        mutedText: color('--p-text-muted-color', '--color-slate-500'),
        border: color('--p-content-border-color', '--theme-border'),
        fontFamily: typography.fontFamily,
        bodyFontSize,
        supportingFontSize: Math.max(12, bodyFontSize - 2),
        reducedMotion: view.matchMedia('(prefers-reduced-motion: reduce)').matches,
      });
    });
  }

  private chartPresentation() {
    const theme = this.chartTheme();
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: theme?.reducedMotion ? (false as const) : { duration: 300 },
      color: theme?.text,
      font: { family: theme?.fontFamily, size: theme?.supportingFontSize, lineHeight: 1.5 },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: theme?.expense,
          titleColor: theme?.surface,
          bodyColor: theme?.surface,
          padding: 12,
          cornerRadius: 8,
          titleFont: {
            family: theme?.fontFamily,
            size: theme?.bodyFontSize,
            weight: 600,
            lineHeight: 1.5,
          },
          bodyFont: { family: theme?.fontFamily, size: theme?.bodyFontSize, lineHeight: 1.5 },
          callbacks: {
            label: (context: { dataset: { label?: string }; label: string; raw: unknown }) =>
              `${context.dataset.label || context.label}: ${Number(context.raw).toLocaleString(
                'th-TH',
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )} บาท`,
          },
        },
      },
    };
  }

  ngOnInit(): void {
    void this.loadDashboard();
  }

  async applyFilters(): Promise<void> {
    const range = {
      dateFrom: toDateKey(this.pendingDateFrom()),
      dateTo: toDateKey(this.pendingDateTo()),
    };
    if (!range.dateFrom || !range.dateTo) {
      this.overlay.error('ช่วงวันที่ไม่ครบ', 'กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด');
      return;
    }
    if (!validDateRange(range)) {
      this.overlay.error('ช่วงวันที่ไม่ถูกต้อง', 'วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มต้น');
      return;
    }
    await this.loadDashboard(range);
  }

  async loadDashboard(range = this.range()): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.error.set('');
    try {
      const data = await this.dashboardService.getDashboard(range);
      if (this.destroyRef.destroyed) return;
      this.dashboard.set(data);
      this.range.set(range);
    } catch (error: unknown) {
      if (this.destroyRef.destroyed) return;
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.error.set(
        `โหลดข้อมูลไม่สำเร็จ · ${message}${this.dashboard() ? ' · กำลังแสดงข้อมูลที่โหลดสำเร็จครั้งล่าสุด' : ''}`,
      );
    } finally {
      if (!this.destroyRef.destroyed) this.isLoading.set(false);
    }
  }
}
